import type { Aircraft } from "../aircraft/Aircraft";
import type { WorldBounds } from "../airport/AirportDefinition";
import type { GameplayConfig } from "../config/gameplay";
import {
  difficultyAt,
  pickSpawnCandidate,
  scoreSpawnCandidate,
  type SpawnOptions,
  type SpawnWarning,
} from "../simulation/SpawnSystem";

/** What the director needs to read from the session, per step. */
export interface SpawnContext {
  readonly bounds: WorldBounds;
  readonly aircraft: readonly Aircraft[];
  readonly elapsed: number;
  readonly random: () => number;
}

/**
 * Decides when traffic arrives and from where.
 *
 * Owns the arrival timer, the pending warnings and the edge history. Draws
 * from the session's seeded generator in a fixed order — the number and
 * sequence of `random()` calls is what the determinism scenario pins, so any
 * change here has to preserve it.
 */
export class SpawnDirector {
  warnings: SpawnWarning[] = [];
  enabled = true;

  private timer = 0.25;
  private nextWarningId = 1;
  private lastEdge: number | null = null;
  private readonly options: SpawnOptions;

  constructor(private readonly gameplay: GameplayConfig) {
    this.options = {
      lookaheadSeconds: gameplay.spawnLookaheadSeconds,
      separationFactor: gameplay.spawnSeparationFactor,
      departureSpeed: gameplay.departureSpeed,
      offset: gameplay.spawnOffset,
      attempts: gameplay.spawnAttempts,
    };
  }

  reset(): void {
    this.warnings = [];
    this.timer = 0.25;
    this.nextWarningId = 1;
    this.lastEdge = null;
  }

  setEnabled(enabled: boolean): void {
    if (enabled && !this.enabled) {
      this.timer = Math.min(this.timer, 0.25);
    }
    this.enabled = enabled;
    if (!enabled) {
      this.warnings = [];
    }
  }

  /**
   * Age the pending warnings.
   *
   * @returns the ones whose countdown finished and that still have room to
   * arrive; the caller turns those into aircraft.
   */
  advance(deltaSeconds: number, context: SpawnContext): SpawnWarning[] {
    for (const warning of this.warnings) {
      warning.warningRemaining -= deltaSeconds;
    }
    const due = this.warnings.filter(
      (warning) => warning.warningRemaining <= 0,
    );
    const pending = this.warnings.filter(
      (warning) => warning.warningRemaining > 0,
    );

    const arrivals: SpawnWarning[] = [];
    for (const warning of due) {
      const arriving: SpawnWarning = { ...warning, warningRemaining: 0 };
      // Traffic moved while it was inbound — re-roll instead of spawning on top.
      if (
        scoreSpawnCandidate(arriving, context.aircraft, pending, this.options) <
        0
      ) {
        const replacement = pickSpawnCandidate(
          context.bounds,
          this.gameplay.spawnWarningSeconds,
          warning.id,
          context.aircraft,
          pending,
          this.lastEdge,
          context.random,
          this.options,
        );
        pending.push(replacement);
        this.lastEdge = replacement.edge;
        continue;
      }
      arrivals.push(arriving);
    }

    this.warnings = pending;
    return arrivals;
  }

  /** Tick the arrival clock; may book one new inbound. */
  schedule(deltaSeconds: number, context: SpawnContext): void {
    if (!this.enabled) {
      return;
    }

    const difficulty = difficultyAt(
      context.elapsed,
      this.gameplay.difficulty,
      this.gameplay.rampSeconds,
    );
    const inbound =
      context.aircraft.filter(
        (item) => item.state === "flying" || item.state === "landing",
      ).length + this.warnings.length;

    this.timer -= deltaSeconds;
    if (this.timer > 0 || inbound >= difficulty.maxConcurrent) {
      return;
    }

    const candidate = pickSpawnCandidate(
      context.bounds,
      this.gameplay.spawnWarningSeconds,
      this.nextWarningId++,
      context.aircraft,
      this.warnings,
      this.lastEdge,
      context.random,
      this.options,
    );
    this.warnings.push(candidate);
    this.lastEdge = candidate.edge;
    this.timer = difficulty.interval * (0.85 + context.random() * 0.3);
  }
}
